import { ApiError, NetworkError } from "@/lib/apiClient";

export type OAuthDiagnostic = { label: string; value: string };

/** Public error fields only: never retain a request, signed query or full response. */
export class OAuthRequestError extends Error {
  constructor(message: string, readonly code: string, readonly diagnostics: OAuthDiagnostic[]) {
    super(message);
    this.name = "OAuthRequestError";
  }
}

/** Provider text is untrusted. Render as text and remove embedded credentials. */
export function safeOAuthErrorText(value: string) {
  return value
    .replace(/https?:\/\/[^\s<>"']+/gi, (raw) => {
      try {
        const url = new URL(raw);
        return `${url.origin}${url.pathname}${url.search || url.hash ? " [parameters removed]" : ""}`;
      } catch { return "[URL removed]"; }
    })
    .replace(/\bBearer\s+[^\s,;]+/gi, "Bearer [redacted]")
    .replace(/\b(oauth_query|client_secret|access_token|refresh_token|id_token|password|sig|state|code_challenge)\s*[:=]\s*(?:"[^"\n]*"|'[^'\n]*'|[^\s,;]+)/gi, "$1=[redacted]");
}

export function oauthErrorDiagnostics(error: unknown): OAuthDiagnostic[] {
  if (!error) return [];
  let rows: OAuthDiagnostic[];
  if (error instanceof OAuthRequestError) {
    rows = [{ label: "Source", value: "Browser validation" }, { label: "Error code", value: error.code }, ...error.diagnostics];
  } else if (error instanceof ApiError) {
    rows = [{ label: "Source", value: "Authorization server" }, { label: "HTTP status", value: String(error.status) }];
    if (error.code) rows.push({ label: "Error code", value: error.code });
    const details = error.details;
    if (details && typeof details === "object" && "providerDescription" in details && typeof details.providerDescription === "string") {
      rows.push({ label: "Server description", value: details.providerDescription });
    }
    if (details && typeof details === "object" && "providerMessage" in details && typeof details.providerMessage === "string") {
      rows.push({ label: "Server message", value: details.providerMessage });
    }
    if (!rows.some(row => row.label.startsWith("Server "))) rows.push({ label: "Message", value: error.message });
  } else if (error instanceof NetworkError) {
    rows = [{ label: "Source", value: "Network" }, { label: "Message", value: "No HTTP response was received. Check your connection and retry." }];
  } else {
    rows = [{ label: "Source", value: "Connection" }, { label: "Message", value: error instanceof Error ? error.message : "Authorization could not continue." }];
  }
  return rows.map(row => ({ ...row, value: safeOAuthErrorText(row.value) }));
}
